import { createRouter, createWebHistory } from 'vue-router'
import DashboardView from '@/views/DashboardView.vue'
import { RouteName, routePath } from '@/routes'
import MineView from '@/views/MineView.vue'
import FriendsView from '@/views/FriendsView.vue'
import EarnView from '@/views/EarnView.vue'
import HomeView from '@/views/HomeView.vue'
import RewardsView from '@/views/RewardsView.vue'
import webapp from '@/services/webapp'
import BoostView from '@/views/BoostView.vue'
import PocView from '@/views/PocView.vue'
import TokensView from '@/views/TokensView.vue'
import { syncTap } from '@/composables/game/tap'
import ProfileView from '@/views/ProfileView.vue'
import SettingsView from '@/views/SettingsView.vue'
import BalanceView from '@/views/BalanceView.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import TutorialsView from '@/views/TutorialsView.vue'
import LeaderboardView from '@/views/LeaderboardView.vue'
import LingoGiveawayView from '@/partners/lingo/lingo-giveaway/views/LingoGiveawayView.vue'
import ShopView from '@/views/ShopView.vue'
import DailyRewardView from '@/views/DailyRewardView.vue'
import SkinsView from '@/views/SkinsView.vue'
import GiveawayView from '@/views/LuckyRushView.vue'
import MysteryBoxView from '@/views/MysteryBoxView.vue'
import { ref, watch } from 'vue'

const { addHapticFeedback } = useHapticFeedback()

const pathAndName = (routeName: RouteName) => ({
  path: routePath[routeName],
  name: routeName,
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      ...pathAndName(RouteName.HOME),
      component: DashboardView,
    },
    {
      ...pathAndName(RouteName.MINE),
      component: MineView,
    },
    {
      ...pathAndName(RouteName.FRIENDS),
      component: FriendsView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.EARN),
      component: EarnView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.POC),
      component: PocView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.REWARDS),
      component: RewardsView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.GIVEAWAY),
      component: GiveawayView,
      meta: {
        hideHeader: true,
        hideNavbar: true,
      },
    },
    {
      ...pathAndName(RouteName.LEADERBOARD),
      component: LeaderboardView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.BOOST),
      component: BoostView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.TOKENS),
      component: TokensView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.PROFILE),
      component: ProfileView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.SETTINGS),
      component: SettingsView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.BALANCE),
      component: BalanceView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.SHOP),
      component: ShopView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.DAILY_REWARDS),
      component: DailyRewardView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.SKINS),
      component: SkinsView,
      meta: {
        hideHeader: true,
      },
    },
    {
      ...pathAndName(RouteName.TUTORIALS),
      component: TutorialsView,
      meta: {
        hideHeader: true,
        hideNavbar: true,
      },
    },
    {
      ...pathAndName(RouteName.MYSTERY_BOX),
      component: MysteryBoxView,
      meta: {
        hideHeader: true,
        hideNavbar: true,
      },
    },
    {
      ...pathAndName(RouteName.MUI),
      component: HomeView,
    },
    // Partner pages
    {
      ...pathAndName(RouteName.LINGO_GIVEAWAY),
      component: LingoGiveawayView,
      meta: {
        hideHeader: true,
        hideNavbar: false,
      },
    },
  ],
  scrollBehavior(to) {
    const scrollTarget = document.querySelector('.scroll-container')

    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }

    if (scrollTarget) {
      scrollTarget.scrollTop = 0
    }

    return { behavior: 'smooth' }
  },
})

export const backButtonCallback = ref<(() => unknown) | null>(null)

const backButtonDefaultCallback = async () => {
  addHapticFeedback('medium')
  await router.push({ name: RouteName.HOME })
}

webapp.setBackButtonCallback(backButtonDefaultCallback)

router.afterEach(async (to) => {
  await syncTap()
  webapp.changeBackButtonVisibility(to.name?.toString() !== RouteName.HOME)
})

watch(backButtonCallback, backButtonCallback => {
  webapp.changeBackButtonVisibility(backButtonCallback !== null)
  webapp.setBackButtonCallback(backButtonCallback ?? backButtonDefaultCallback)
})

export default router
